import { mapValues } from 'es-toolkit';
import React, { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Detailed } from '@react-three/drei';
import { ThreeElements, useLoader } from '@react-three/fiber';
import { ErrorBoundary } from 'react-error-boundary';
import {
  BufferGeometry,
  EdgesGeometry,
  Euler,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
} from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

import {
  Bearing,
  Component,
  ConnectingHardware,
  Hanger,
  isTruss,
  Point3D,
  StandardConnectingHardwareModel,
} from '@paragon/contract-library';
import { BearingVisualData } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import hhus26_2Model from '../../../resources/hangerModels/HHUS26-2.stl';
import hhus28_2Model from '../../../resources/hangerModels/HHUS28-2.stl';
import hus26Model from '../../../resources/hangerModels/HUS26.stl';
import hus28Model from '../../../resources/hangerModels/HUS28.stl';
import lthja26Model from '../../../resources/hangerModels/LTHJA26.stl';
import lus24Model from '../../../resources/hangerModels/LUS24.stl';
import lus24_2Model from '../../../resources/hangerModels/LUS24-2.stl';
import lus26Model from '../../../resources/hangerModels/LUS26.stl';
import lus28Model from '../../../resources/hangerModels/LUS28.stl';
import mus26Model from '../../../resources/hangerModels/MUS26.stl';
import mus28Model from '../../../resources/hangerModels/MUS28.stl';
import thja26Model from '../../../resources/hangerModels/THJA26.stl';
import tjc37Model from '../../../resources/hangerModels/TJC37.stl';
import { useAppDispatch } from '../../components/hooks';
import { LAYOUT_TO_DESIGN_COORDINATES_MATRIX } from '../../library/layout-geometry/coordinates';
import { handleStandardSceneItemRightClick, leftClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { isHangerSupportSideFromRight } from '../component/BearingVisual';
import { Merged } from './Instances';
import { layoutColors } from './layoutColors';

useLoader.preload(STLLoader, hhus26_2Model);
useLoader.preload(STLLoader, hhus28_2Model);
useLoader.preload(STLLoader, hus26Model);
useLoader.preload(STLLoader, hus28Model);
useLoader.preload(STLLoader, lthja26Model);
useLoader.preload(STLLoader, lus24Model);
useLoader.preload(STLLoader, lus24_2Model);
useLoader.preload(STLLoader, lus26Model);
useLoader.preload(STLLoader, lus28Model);
useLoader.preload(STLLoader, mus26Model);
useLoader.preload(STLLoader, mus28Model);
useLoader.preload(STLLoader, thja26Model);
useLoader.preload(STLLoader, tjc37Model);

const hangerMaterial = new MeshBasicMaterial({ color: layoutColors.hangers });
const hangerEdgesMaterial = new LineBasicMaterial();

const SUPPORTED_HANGERS = [
  'HHUS26_2',
  'HHUS28_2',
  'HUS26',
  'HUS28',
  'LTHJA26',
  'LUS24',
  'LUS24_2',
  'LUS26',
  'LUS28',
  'MUS26',
  'MUS28',
  'THJA26',
  'TJC37',
] as const satisfies StandardConnectingHardwareModel[];
type SupportedHangerType = (typeof SUPPORTED_HANGERS)[number];
export const lookupHangerMeshKey = (connector: ConnectingHardware): SupportedHangerType | null =>
  connector.standardOrCustom === 'Standard' &&
  (SUPPORTED_HANGERS as StandardConnectingHardwareModel[]).includes(connector.model)
    ? connector.model
    : null;

type HangerRecord<T, O = false> = O extends false
  ? { readonly [K in SupportedHangerType]: T }
  : { readonly [K in SupportedHangerType]?: T };
type HangerGeometries = HangerRecord<BufferGeometry>;
type HangerMeshes = HangerRecord<Mesh>;
type HangerMeshComponents = HangerRecord<React.JSXElementConstructor<ThreeElements['object3D']>>;
type HangerEdges = HangerRecord<LineSegments>;
type HangerMatrices = HangerRecord<Matrix4, true>;

const context = createContext<{
  hangerMeshes: HangerMeshComponents;
  edgeSegments: HangerEdges;
} | null>(null);

const hangerMatrices: HangerMatrices = {
  TJC37: new Matrix4()
    .makeTranslation(0, 1.75, 0)
    .multiply(new Matrix4().makeRotationFromEuler(new Euler(Math.PI / 2))),
};

// Simpson's models show nail holes as dodecagons or higher-degree polygons, so the external angle between faces is
// roughly 30 degrees. We choose not to render edges if the angle is less than 35 to avoid proliferation of tiny
// rendered segments.
const hangerEdgeRenderTolerance = 35;

export function HangerMeshProvider({ children }: { readonly children: ReactNode }) {
  return (
    <ErrorBoundary fallback={children}>
      <HangerMeshProviderWithoutErrorHandling>{children}</HangerMeshProviderWithoutErrorHandling>
    </ErrorBoundary>
  );
}

function HangerMeshProviderWithoutErrorHandling({ children }: PropsWithChildren) {
  const hangerGeometries: HangerGeometries = {
    HHUS26_2: useLoader(STLLoader, hhus26_2Model),
    HHUS28_2: useLoader(STLLoader, hhus28_2Model),
    HUS26: useLoader(STLLoader, hus26Model),
    HUS28: useLoader(STLLoader, hus28Model),
    LTHJA26: useLoader(STLLoader, lthja26Model),
    LUS24: useLoader(STLLoader, lus24Model),
    LUS24_2: useLoader(STLLoader, lus24_2Model),
    LUS26: useLoader(STLLoader, lus26Model),
    LUS28: useLoader(STLLoader, lus28Model),
    MUS26: useLoader(STLLoader, mus26Model),
    MUS28: useLoader(STLLoader, mus28Model),
    THJA26: useLoader(STLLoader, thja26Model),
    TJC37: useLoader(STLLoader, tjc37Model),
  };

  const edgeGeometries: HangerGeometries = useState(() =>
    mapValues(hangerGeometries, (geometry) => new EdgesGeometry(geometry, hangerEdgeRenderTolerance)),
  )[0];

  const hangerMeshes: HangerMeshes = useState(() =>
    mapValues(hangerGeometries, (geometry) => new Mesh(geometry, hangerMaterial)),
  )[0];

  const edgeSegments: HangerEdges = useState(() =>
    mapValues(edgeGeometries, (geometry) => new LineSegments(geometry, hangerEdgesMaterial)),
  )[0];

  useEffect(
    () => () => {
      for (const mesh of Object.values(hangerMeshes)) mesh.geometry.dispose();
    },
    [hangerMeshes],
  );

  useEffect(
    () => () => {
      for (const mesh of Object.values(edgeSegments)) mesh.geometry.dispose();
    },
    [edgeSegments],
  );

  return (
    // initialSize is 0 to reduce overhead for hangers that aren't used in this project
    <Merged meshes={hangerMeshes} initialSize={0} sizeIncrement={50}>
      {(instances: HangerMeshComponents) => (
        <HangerContextProvider mergedMeshInstances={instances} edgeSegments={edgeSegments}>
          {children}
        </HangerContextProvider>
      )}
    </Merged>
  );
}

function HangerContextProvider(
  props: PropsWithChildren<{ mergedMeshInstances: HangerMeshComponents; edgeSegments: HangerEdges }>,
) {
  const contextValue = useMemo(
    () => ({ hangerMeshes: props.mergedMeshInstances, edgeSegments: props.edgeSegments }),
    [props.mergedMeshInstances, props.edgeSegments],
  );

  return <context.Provider value={contextValue}>{props.children}</context.Provider>;
}

interface BasicHangerProps {
  readonly hanger: Hanger;
  readonly component: Component;
  readonly wireframe?: boolean;
}

type ModeProps =
  | {
      readonly mode: 'Layout';
      readonly numberOfPlies: number;
    }
  | {
      readonly mode: 'Girder Design';
    };

interface ManualDetailProps {
  readonly lowDetail?: boolean;
}

export function JackHangerVisual(props: {
  hardware: StandardConnectingHardwareModel;
  component: Component;
  visualData: BearingVisualData;
  bearing: Bearing;
}) {
  const { visualData, component, bearing, hardware } = props;

  const z = componentThickness(component, isTruss(component) ? component.numberOfPlies : 1) / 2;

  const { position, width } = visualData;
  const rightSide = isHangerSupportSideFromRight(position, component);

  return (
    <BaseHangerVisual
      connector={{ standardOrCustom: 'Standard', model: hardware }}
      position={{ x: position.x + (rightSide ? width / 2 : -width / 2), y: position.y, z }}
      rotationY={rightSide ? -Math.PI / 2 : Math.PI / 2}
      raycast={{ type: 'bearing', bearing }}
    />
  );
}

function HangerVisualManualDetail(props: BasicHangerProps & ModeProps & ManualDetailProps) {
  const { hanger, component, mode, wireframe, lowDetail } = props;

  // In single component design view, we only render a single ply of the component, no matter how many plies it has.
  const renderedPlies = mode === 'Layout' ? props.numberOfPlies : 1;
  const componentPlyScaledThickness = componentThickness(component, renderedPlies);

  return (
    <BaseHangerVisual
      connector={hanger.connector}
      position={{
        ...hanger.locationOnGirder,
        z: hanger.locationType === 'FrontFace' ? componentPlyScaledThickness : 0,
      }}
      rotationY={hanger.locationType === 'BackFace' ? Math.PI : 0}
      wireframe={wireframe}
      lowDetail={lowDetail}
      raycast={
        mode === 'Layout'
          ? undefined
          : mode === 'Girder Design'
            ? { type: 'hanger', hanger }
            : assertExhaustiveSwitch(mode)
      }
    />
  );
}

function BaseHangerVisual(props: {
  connector: ConnectingHardware;
  position: Point3D;
  rotationY: number;
  wireframe?: boolean;
  lowDetail?: boolean;
  raycast?: { type: 'bearing'; bearing: Bearing } | { type: 'hanger'; hanger: Hanger };
}) {
  const { connector, position, rotationY, wireframe, lowDetail, raycast } = props;

  const dispatch = useAppDispatch();

  const contextValue = useContext(context);

  const hangerMeshKey = lookupHangerMeshKey(connector);

  const HangerMesh = contextValue && hangerMeshKey && !wireframe ? contextValue.hangerMeshes[hangerMeshKey] : null;
  const edgeSegmentObject =
    contextValue && hangerMeshKey && (wireframe || !lowDetail) ? contextValue.edgeSegments[hangerMeshKey] : null;

  const edgeSegmentsForThisInstance = useMemo(() => edgeSegmentObject?.clone(false), [edgeSegmentObject]);

  let eventHandlers: Partial<ThreeElements['object3D']> = {};
  if (!wireframe && raycast) {
    const item = {
      type: raycast.type,
      guid: raycast.type === 'bearing' ? raycast.bearing.guid : raycast.hanger.guid,
      origin: 'fiber',
    } as const;

    eventHandlers = {
      onClick: (event) =>
        dispatch(
          leftClickItem(
            event,
            raycast.type === 'bearing'
              ? { item: raycast.bearing, type: raycast.type }
              : { item: raycast.hanger, type: raycast.type },
          ),
        ),
      onContextMenu: async (event) => await dispatch(handleStandardSceneItemRightClick(event, item)),
      onPointerOver: (event) => {
        event.stopPropagation();
        dispatch(ensureHoverItemIsActive(item));
      },
      onPointerOut: () => dispatch(clearHoverItemIfActive(item)),
    };
  }

  const hangerMatrix = (hangerMeshKey && hangerMatrices[hangerMeshKey]) ?? LAYOUT_TO_DESIGN_COORDINATES_MATRIX;

  return (
    <group position={[position.x, position.y, position.z]} rotation-y={rotationY}>
      <group matrix={hangerMatrix} matrixAutoUpdate={false}>
        {HangerMesh ? <HangerMesh {...eventHandlers} /> : null}
        {!contextValue || !hangerMeshKey ? (
          <mesh {...eventHandlers}>
            <meshBasicMaterial color={layoutColors.hangers} />
            <sphereGeometry args={[1]} />
          </mesh>
        ) : null}
        {edgeSegmentsForThisInstance ? <primitive object={edgeSegmentsForThisInstance} /> : null}
      </group>
    </group>
  );
}

function componentThickness(component: Component, plies: number) {
  return Math.max(...component.members.map(({ thickness }) => thickness)) * plies;
}

export function HangerVisual(props: BasicHangerProps & ModeProps) {
  const LAYOUT_HIGH_DETAIL_THRESHOLD = 12 * 12;
  switch (props.mode) {
    case 'Layout':
      return (
        <Detailed distances={[0, LAYOUT_HIGH_DETAIL_THRESHOLD]}>
          <HangerVisualManualDetail {...props} />
          <HangerVisualManualDetail {...props} lowDetail />
        </Detailed>
      );
    case 'Girder Design':
      return <HangerVisualManualDetail {...props} />;
    default:
      assertExhaustiveSwitch(props);
  }
}
